import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Users,
  UserPlus,
  Sparkles,
  ArrowLeft,
  Shield,
  CheckCircle,
  Zap,
  MessageCircle,
  Sun,
  Star,
  Globe,
  Target,
  Award,
  ArrowRight,
  ShieldCheck,
  Cpu,
  Cloud,
} from "lucide-react";
import Login from "./Login";
import Register from "./Register";
import logo from "../../images/logo2.png";
import Toast from "./Toast";
import { useNavigate } from "react-router-dom";

const Auth = () => {
  const [activeTab, setActiveTab] = useState("login");
  const navigate = useNavigate();

  const features = [
    {
      icon: MessageCircle,
      text: "WhatsApp API Integration",
      gradient: "from-amber-500 to-orange-400",
    },
    {
      icon: Zap,
      text: "smartitbox-Powered Automation",
      gradient: "from-yellow-500 to-orange-300",
    },
    {
      icon: ShieldCheck,
      text: "Enterprise Security",
      gradient: "from-orange-500 to-amber-400",
    },
    {
      icon: Cpu,
      text: "Real-time Analytics",
      gradient: "from-yellow-600 to-orange-500",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
    },
  };

  const gradientBackground = `
    radial-gradient(circle at 20% 30%, rgba(251, 191, 36, 0.15) 0%, transparent 50%),
    radial-gradient(circle at 80% 70%, rgba(249, 115, 22, 0.12) 0%, transparent 50%),
    radial-gradient(circle at 40% 80%, rgba(245, 158, 11, 0.1) 0%, transparent 50%),
    linear-gradient(to bottom, #0f172a, #1e293b)
  `;

  const floatAnimation = {
    animate: {
      y: [0, -20, 0],
      transition: {
        duration: 6,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row overflow-hidden bg-gradient-to-br from-amber-50/30 via-white to-orange-50/30">
      {/* Left Side - Brand & Features */}
      <motion.div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      >
        {/* Background */}
        <div
          className="absolute inset-0"
          style={{ background: gradientBackground }}
        >
          {/* Sun rays pattern */}
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              animate={{
                rotate: 360,
              }}
              transition={{
                duration: 60 + i * 5,
                repeat: Infinity,
                ease: "linear",
              }}
              className="absolute top-1/2 left-1/2 w-full max-w-4xl h-px bg-gradient-to-r from-transparent via-amber-500/10 to-transparent"
              style={{ transform: `rotate(${i * 45}deg)` }}
            />
          ))}
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Hero Content */}
          <motion.div
            className="max-w-lg"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={itemVariants}>
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-900/40 to-orange-900/30 backdrop-blur-md rounded-full px-5 py-2.5 mb-8 border border-amber-700/30 shadow-lg"
              >
                <span className="text-sm font-semibold text-amber-200">
                  Trusted by 10,000+ Businesses
                </span>
                <Star className="w-4 h-4 text-orange-400" />
              </motion.div>
            </motion.div>

            <motion.h1
              className="text-3xl sm:text-4xl font-bold text-white mb-8 leading-tight"
              variants={itemVariants}
            >
              <span className="relative">
                <span className="bg-gradient-to-r from-amber-200 via-orange-200 to-amber-200 bg-clip-text text-transparent">
                  Transform Your
                </span>
                <motion.div
                  animate={{ opacity: [0.3, 0.6, 0.3] }}
                  transition={{ duration: 4, repeat: Infinity }}
                  className="absolute -inset-1 bg-gradient-to-r from-amber-500/20 via-orange-500/10 to-yellow-500/20 blur-xl"
                />
              </span>
              <br />
              <span className="relative">
                <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
                  Business Communication
                </span>
                <motion.span
                  animate={{ width: ["0%", "100%", "0%"] }}
                  transition={{ duration: 5, repeat: Infinity }}
                  className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent"
                />
              </span>
            </motion.h1>

            <motion.p
              className="text-xl text-amber-100/80 mb-10 leading-relaxed"
              variants={itemVariants}
            >
              Enterprise-grade WhatsApp automation platform for seamless
              customer engagement and efficient{" "}
              <span className="font-semibold text-amber-300">
                workflow management.
              </span>
              .
            </motion.p>

            {/* Features List */}
            <motion.div className="space-y-6" variants={itemVariants}>
              {features.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <motion.div
                    key={index}
                    className="flex items-center gap-5 group"
                    whileHover={{ x: 5, scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                  >
                    <div className="relative">
                      <motion.div
                        animate={{
                          scale: [1, 1.1, 1],
                        }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                        className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} rounded-xl blur-md opacity-20`}
                      />
                      <div
                        className={`relative w-12 h-12 bg-gradient-to-br ${feature.gradient}/10 backdrop-blur-sm rounded-xl flex items-center justify-center border border-amber-500/30 group-hover:border-amber-400/50 transition-all duration-300`}
                      >
                        <IconComponent className="w-6 h-6 text-amber-400 group-hover:text-amber-300 transition-colors" />
                      </div>
                    </div>
                    <span className="text-white font-medium text-lg">
                      {feature.text}
                    </span>
                  </motion.div>
                );
              })}
            </motion.div>
          </motion.div>
        </div>

        {/* Central sun decoration */}
        <motion.div
          animate={{
            scale: [1, 1.05, 1],
            rotate: 360,
          }}
          transition={{
            scale: {
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
            },
            rotate: {
              duration: 30,
              repeat: Infinity,
              ease: "linear",
            },
          }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-amber-600/5 to-orange-600/3 rounded-full blur-3xl pointer-events-none"
        />
      </motion.div>

      {/* Right Side - Authentication Form */}
      <motion.div
        className="flex-1 flex flex-col items-center justify-center p-4 sm:p-8 lg:p-12 relative overflow-y-auto"
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      >
        {/* Back button for mobile */}
        <motion.button
          onClick={() => navigate("/")}
          className="absolute top-6 left-6 flex items-center gap-2 text-amber-700 hover:text-orange-700 font-medium lg:hidden"
          whileHover={{ x: -3 }}
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to Home</span>
        </motion.button>

        <div className="w-full max-w-md">
          {/* Logo */}
          <motion.div
            className="mb-2 py-6 flex flex-col items-center"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <img
              src="/logo.png"
              alt="Smart IT Box"
              className="relative h-20 bg-white/90 backdrop-blur-sm rounded-xl p-3 border border-amber-500/30"
            />
          </motion.div>

          {/* Tab Navigation */}
          <motion.div
            className="mb-6"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className="flex gap-1 p-1 bg-gradient-to-r from-amber-50/50 to-orange-50/50 backdrop-blur-sm rounded-2xl border border-amber-200/50">
              <motion.button
                className={`flex-1 flex items-center justify-center gap-3 px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  activeTab === "login"
                    ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg"
                    : "text-amber-900 hover:text-orange-700 bg-transparent"
                }`}
                onClick={() => setActiveTab("login")}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Users className="w-4 h-4" />
                <span className="text-sm">Sign In</span>
              </motion.button>
              <motion.button
                className={`flex-1 flex items-center justify-center gap-3 px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  activeTab === "register"
                    ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg"
                    : "text-amber-900 hover:text-orange-700 bg-transparent"
                }`}
                onClick={() => setActiveTab("register")}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <UserPlus className="w-4 h-4" />
                <span className="text-sm">Sign Up</span>
              </motion.button>
            </div>
          </motion.div>

          {/* Form Container */}
          <motion.div
            className="bg-gradient-to-br from-white/90 to-amber-50/50 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-amber-200/50"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            whileHover={{
              boxShadow: "0 25px 50px -12px rgba(251, 191, 36, 0.1)",
            }}
          >
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: activeTab === "login" ? -20 : 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: activeTab === "login" ? 20 : -20 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              {activeTab === "login" ? <Login /> : <Register />}
            </motion.div>
          </motion.div>
        </div>
      </motion.div>

      {/* Floating decorative elements */}
      {[1, 2, 3].map((i) => (
        <motion.div
          key={i}
          {...floatAnimation}
          animate={{
            ...floatAnimation.animate,
            y: floatAnimation.animate.y.map((y) => y * (i % 2 === 0 ? 1 : -1)),
            transition: {
              ...floatAnimation.animate.transition,
              duration: 5 + i * 2,
            },
          }}
          className={`absolute ${i === 1 ? "top-10 right-10" : i === 2 ? "bottom-10 left-10" : "top-1/2 right-20"} w-24 h-24 rounded-full bg-gradient-to-r from-yellow-300/10 to-orange-300/5 blur-2xl pointer-events-none lg:hidden`}
        />
      ))}

      <Toast />
    </div>
  );
};

export default Auth;
